import { ApolloClient, useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GET_APP_EMBED_STATUS, GET_PROTECTION_SETTINGS } from 'gql/queries';
import { StorePropertiesRefetchResult } from 'types/store';
import { DocumentNode } from 'graphql';
import { readThemeAppEmbedEvidence } from 'utils/themeAppEmbed';
import { boundedInstallationRead, installationAutomaticReadDue, useVisibleInstallationRefresh } from './useVisibleInstallationRefresh';

const FRESH_FOR_MS = 5 * 60 * 1000;
type Errors = { store: boolean; protection: boolean; embed: boolean };
type StoreCheckResult = StorePropertiesRefetchResult & {
  error?: unknown;
  errors?: readonly unknown[];
};
type Check = {
  settled: boolean;
  checkedAt?: number;
  attemptedAt?: number;
  generation: number;
  revision: number;
  attemptedRevision: number;
  pending?: Promise<void>;
  controller?: AbortController;
  followUp: boolean;
  errors: Errors;
  listeners: Set<() => void>;
};
const checks = new WeakMap<object, Map<string, Check>>();
const noErrors = (): Errors => ({
  store: false,
  protection: false,
  embed: false,
});
const notify = (check: Check) =>
  check.listeners.forEach((listener) => listener());

function getCheck(client: ApolloClient<object>, key: string): Check {
  let entries = checks.get(client);
  if (!entries) {
    entries = new Map();
    checks.set(client, entries);
    const reset = async () => {
      entries!.forEach((check) => {
        // Late checks from the previous authentication/cache generation are ignored.
        check.generation++;
        check.controller?.abort();
        check.controller = undefined;
        check.checkedAt = undefined;
        check.attemptedAt = undefined;
        check.pending = undefined;
        check.settled = false;
        check.followUp = false;
        check.errors = noErrors();
        notify(check);
      });
    };
    client.onClearStore(reset);
    client.onResetStore(reset);
  }
  let check = entries.get(key);
  if (!check) {
    check = {
      settled: false,
      generation: 0,
      revision: 0,
      attemptedRevision: 0,
      followUp: false,
      errors: noErrors(),
      listeners: new Set(),
    };
    entries.set(key, check);
  }
  return check;
}

export function useHomeSetupRefresh({
  storeId,
  userId,
  role,
  refetchStore,
}: {
  storeId: string;
  userId: string;
  role?: string;
  refetchStore: () => Promise<StoreCheckResult>;
}) {
  const client = useApolloClient();
  const check = useMemo(
    () => getCheck(client, JSON.stringify([userId, role, storeId])),
    [client, userId, role, storeId],
  );
  const storeRefetch = useRef(refetchStore);
  storeRefetch.current = refetchStore;
  const [, rerender] = useState(0);

  const refresh = useCallback(
    function run(force = false): Promise<void> | undefined {
      if (!check.listeners.size) return;
      if (check.pending) {
        if (check.revision !== check.attemptedRevision) check.followUp = true;
        return check.pending;
      }
      const lastCheck = check.checkedAt ?? check.attemptedAt;
      if (
        !force &&
        check.revision === check.attemptedRevision &&
        lastCheck !== undefined &&
        Date.now() - lastCheck < FRESH_FOR_MS
      )
        return;

      const generation = check.generation;
      const controller = new AbortController();
      check.controller = controller;
      check.attemptedAt = Date.now();
      check.attemptedRevision = check.revision;
      const storeRequest = storeRefetch.current;
      const readQuery = async (query: DocumentNode) => {
        // Only a response settled within this check may replace cached evidence.
        // Disabling deduplication lets an explicit retry escape a hung older read.
        const result = await boundedInstallationRead(client.query({
          query, variables: { storeId }, fetchPolicy: 'no-cache',
          context: { queryDeduplication: false, fetchOptions: { signal: controller.signal } },
        }), controller);
        if (generation === check.generation && check.listeners.size && !controller.signal.aborted
          && !result.error && !result.errors?.length && result.data) {
          client.writeQuery({ query, variables: { storeId }, data: result.data });
        }
        return result;
      };
      check.pending = Promise.allSettled([
        readQuery(GET_PROTECTION_SETTINGS)
          .then(
            ({ data, errors, error }) =>
              !error &&
              !errors?.length &&
              Array.isArray(data?.protectionSettings) &&
              data.protectionSettings.every(
                (row: { storeId: string }) => row?.storeId === storeId,
              ),
          ),
        readQuery(GET_APP_EMBED_STATUS)
          .then(
            ({ data, errors, error }) =>
              !error &&
              !errors?.length &&
              ['enabled', 'disabled'].includes(readThemeAppEmbedEvidence(
                data?.getAppEmbedStatus, storeId, data?.getAppEmbedStatus?.readiness?.shopDomain,
              ).status),
          ),
        boundedInstallationRead(Promise.resolve().then(() => {
          if (controller.signal.aborted) throw new Error('Setup check stopped');
          return storeRequest();
        }), controller)
          .then(
            (result) =>
              !result?.error &&
              !result?.errors?.length &&
              result?.data?.store?.id === storeId,
          ),
      ])
        .then(([protection, embed, store]) => {
          if (generation !== check.generation) return;
          const failed = (result: PromiseSettledResult<boolean>) =>
            result.status === 'rejected' || !result.value;
          check.errors = {
            protection: failed(protection),
            embed: failed(embed),
            store: failed(store),
          };
          check.settled = true;
          check.checkedAt = Object.values(check.errors).some(Boolean)
            ? undefined
            : Date.now();
        })
        .finally(() => {
          if (generation !== check.generation) return;
          check.pending = undefined;
          check.controller = undefined;
          if (check.followUp) {
            check.followUp = false;
            void run(true);
          } else {
            notify(check);
          }
        });
      notify(check);
      return check.pending;
    },
    [check, client, storeId],
  );

  const generation = check.generation;
  useVisibleInstallationRefresh(storeId && userId ? check : null, () => {
    const lastAttempt = check.attemptedAt;
    if (check.revision === check.attemptedRevision && !installationAutomaticReadDue(lastAttempt)) return;
    void refresh(true);
  });
  useEffect(() => {
    const update = () => rerender((version) => version + 1);
    check.listeners.add(update);
    update();
    if (document.visibilityState !== 'hidden') void refresh();
    return () => {
      check.listeners.delete(update);
      const pendingAtLeave = Boolean(check.pending);
      const generationAtLeave = check.generation;
      // A same-scope immediate remount can rejoin the shared pending check.
      void Promise.resolve().then(() => {
        if (check.listeners.size || !pendingAtLeave || check.generation !== generationAtLeave) return;
        check.generation++;
        check.controller?.abort();
        check.controller = undefined;
        check.pending = undefined;
        check.checkedAt = undefined;
        check.attemptedAt = undefined;
        check.settled = false;
        check.followUp = false;
      });
    };
  }, [check, refresh, generation]);

  return {
    cold: !check.settled,
    refreshing: Boolean(check.pending),
    errors: check.errors,
    refresh: () => refresh(true),
    recheckOnReturn: () => {
      check.revision++;
    },
  };
}
